import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  or,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, functions, storage } from '../../firebase';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import {
  downloadImageUrl,
  filenameForGalleryAsset,
} from '../../lib/downloadImageUrl';
import {
  deleteProjectGalleryAssetClient,
  galleryDocIdForManifestAsset,
  upsertProjectGalleryAssetClient,
} from '../../lib/syncProjectGalleryAsset';
import { evaluateSoleSemanticKeyHolder } from '../../lib/playtestAssetUrlMap';
import { saveEditedAssetVariant } from '../../lib/saveEditedAssetVariant';
import ImageEditorModal from './ImageEditorModal';

const pushGameAssetsToGithubFn = httpsCallable(
  functions,
  'pushGameAssetsToGithub',
);

/**
 * Render Queue "Push selected to GitHub" enablement + tooltip.
 * Exported for sanity checks against the live button rules.
 * @param {{
 *   selectedRows: Array<{ status?: string }>,
 *   repoLinked: boolean,
 *   pushBusy?: boolean,
 *   bulkBusy?: boolean,
 * }} opts
 * @returns {{ canPush: boolean, tooltip: string, allSelectedApproved: boolean }}
 */
export function getPushToGithubUiState(opts) {
  const selectedRows = Array.isArray(opts.selectedRows) ? opts.selectedRows : [];
  const repoLinked = opts.repoLinked === true;
  const pushBusy = opts.pushBusy === true;
  const bulkBusy = opts.bulkBusy === true;
  const allSelectedApproved =
    selectedRows.length > 0 &&
    selectedRows.every((r) => r && r.status === 'approved');

  let tooltip = 'Commit selected approved assets to public/assets/';
  if (!repoLinked) {
    tooltip = 'Link a GitHub repo in Core Config first';
  } else if (!allSelectedApproved) {
    tooltip = 'Approve selected assets before pushing';
  }

  const canPush =
    allSelectedApproved && repoLinked && !pushBusy && !bulkBusy;

  return { canPush, tooltip, allSelectedApproved };
}
/** @typedef {'draft'|'pending'|'generating'|'needs_review'|'approved'|'failed'} AssetQueueStatus */

const ANCHOR_CATEGORIES = [
  'protagonist',
  'enemy',
  'background',
  'ui',
  'prop',
  'effect',
];

/** Statuses that receive a category seed & get re-queued. */
const ANCHOR_REQUEUE_STATUSES = new Set(['pending', 'needs_review', 'draft']);

/**
 * @type {Record<AssetQueueStatus, { label: string, color: 'default'|'info'|'warning'|'success'|'error' }>}
 */
const STATUS_CHIP = {
  draft: { label: 'Draft', color: 'default' },
  pending: { label: 'Pending', color: 'default' },
  generating: { label: 'Generating', color: 'info' },
  needs_review: { label: 'Needs review', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

/** Statuses included in "select all" (pending / reviewable / draft). */
const SELECTABLE_STATUSES = new Set([
  'draft',
  'pending',
  'generating',
  'needs_review',
  'failed',
  'approved',
]);

/** Queue rows that can be cancelled back to draft for a later restart. */
const CANCELLABLE_STATUSES = new Set(['pending', 'generating']);

/**
 * @param {string} status
 * @returns {{ label: string, color: 'default'|'info'|'warning'|'success'|'error' }}
 */
function statusChipProps(status) {
  return STATUS_CHIP[status] || STATUS_CHIP.pending;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function timestampMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * @param {object} asset
 * @param {string} manifestId
 * @param {number} index
 * @returns {string}
 */
function resolveAssetId(asset, manifestId, index) {
  if (asset && typeof asset.id === 'string' && asset.id.trim()) {
    return asset.id.trim();
  }
  return `asset_${manifestId || 'row'}_${index + 1}`;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeCategoryKey(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * @param {object} asset
 * @returns {string}
 */
function assetCategoryKey(asset) {
  return (
    normalizeCategoryKey(asset?.category) ||
    normalizeCategoryKey(asset?.type) ||
    ''
  );
}

/**
 * Flatten nested manifest.assets[] into queue rows for the active project.
 * @param {Array<object>} manifests
 * @returns {Array<object>}
 */
export function flattenManifestAssets(manifests) {
  const rows = Array.isArray(manifests) ? [...manifests] : [];
  rows.sort(
    (a, b) =>
      timestampMs(b.updatedAt || b.createdAt) -
      timestampMs(a.updatedAt || a.createdAt),
  );

  /** @type {Map<string, object>} */
  const byAssetId = new Map();

  rows.forEach((manifest) => {
    const manifestId = typeof manifest.id === 'string' ? manifest.id : '';
    const assets = Array.isArray(manifest.assets) ? manifest.assets : [];
    assets.forEach((asset, index) => {
      if (!asset || typeof asset !== 'object') return;
      const assetId = resolveAssetId(asset, manifestId, index);
      if (byAssetId.has(assetId)) return;
      byAssetId.set(assetId, {
        id: assetId,
        category:
          (typeof asset.category === 'string' && asset.category.trim()) ||
          (typeof asset.type === 'string' && asset.type.trim()) ||
          '—',
        prompt: typeof asset.prompt === 'string' ? asset.prompt : '',
        status: typeof asset.status === 'string' ? asset.status : 'pending',
        previewUrl:
          (typeof asset.previewUrl === 'string' && asset.previewUrl.trim()) ||
          (typeof asset.imageUrl === 'string' && asset.imageUrl.trim()) ||
          '',
        imageUrl:
          (typeof asset.imageUrl === 'string' && asset.imageUrl.trim()) ||
          (typeof asset.previewUrl === 'string' && asset.previewUrl.trim()) ||
          '',
        sourceAssetId:
          typeof asset.sourceAssetId === 'string' && asset.sourceAssetId.trim()
            ? asset.sourceAssetId.trim()
            : '',
        storagePath:
          typeof asset.storagePath === 'string' ? asset.storagePath : '',
        errorMessage:
          typeof asset.errorMessage === 'string' ? asset.errorMessage : '',
        referenceUrl:
          typeof asset.referenceUrl === 'string' ? asset.referenceUrl : '',
        manifestId,
        manifestAssetId: assetId,
      });
    });
  });

  return [...byAssetId.values()];
}

/**
 * Dense Render Queue table backed by companies/{companyId}/game_manifests.
 * @param {{
 *   companyId?: string | null,
 *   projectId?: string | null,
 *   title?: string,
 *   dense?: boolean,
 * }} [props]
 */
export default function AssetQueueTable({
  companyId = null,
  projectId = null,
  title = 'Render Queue',
  dense = true,
}) {
  const resolvedCompanyId =
    typeof companyId === 'string' ? companyId.trim() : '';
  const resolvedProjectId =
    typeof projectId === 'string' ? projectId.trim() : '';

  const manifestsQuery = useMemo(() => {
    if (!resolvedCompanyId || !resolvedProjectId) return null;
    return query(
      collection(db, 'companies', resolvedCompanyId, 'game_manifests'),
      or(
        where('projectId', '==', resolvedProjectId),
        where('gameId', '==', resolvedProjectId),
      ),
    );
  }, [resolvedCompanyId, resolvedProjectId]);

  const {
    data: manifests,
    loading,
    error,
  } = useFirestoreQuery(manifestsQuery, {
    enabled: Boolean(manifestsQuery),
  });

  const projectGalleryQuery = useMemo(() => {
    if (!resolvedCompanyId || !resolvedProjectId) return null;
    return collection(
      db,
      'companies',
      resolvedCompanyId,
      'game_projects',
      resolvedProjectId,
      'assets',
    );
  }, [resolvedCompanyId, resolvedProjectId]);

  const {
    data: projectGalleryAssets,
    loading: projectGalleryLoading,
  } = useFirestoreQuery(projectGalleryQuery, {
    enabled: Boolean(projectGalleryQuery),
  });

  /** @type {[string|null, function]} High-res preview dialog URL */
  const [previewImage, setPreviewImage] = useState(null);
  const [previewMeta, setPreviewMeta] = useState({ id: '', category: '' });
  /** @type {[string[], function]} Selected asset ids */
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  /** @type {[object|null, function]} game_projects/{projectId} for GitHub link */
  const [projectDoc, setProjectDoc] = useState(null);

  useEffect(() => {
    if (!resolvedCompanyId || !resolvedProjectId) {
      setProjectDoc(null);
      return undefined;
    }
    const projectRef = doc(
      db,
      'companies',
      resolvedCompanyId,
      'game_projects',
      resolvedProjectId,
    );
    const unsub = onSnapshot(
      projectRef,
      (snap) => {
        setProjectDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      () => {
        setProjectDoc(null);
      },
    );
    return () => unsub();
  }, [resolvedCompanyId, resolvedProjectId]);

  const assets = useMemo(
    () => flattenManifestAssets(Array.isArray(manifests) ? manifests : []),
    [manifests],
  );

  /** Gallery thumbnails: project assets + rendered manifest previews. */
  const galleryItems = useMemo(() => {
    /** @type {Map<string, { id: string, url: string, label: string }>} */
    const byUrl = new Map();

    const addItem = (id, url, label) => {
      const href = typeof url === 'string' ? url.trim() : '';
      if (!href || byUrl.has(href)) return;
      byUrl.set(href, {
        id: String(id || href),
        url: href,
        label: typeof label === 'string' && label.trim() ? label.trim() : 'Asset',
      });
    };

    (Array.isArray(projectGalleryAssets) ? projectGalleryAssets : []).forEach(
      (row) => {
        const url =
          (typeof row.imageUrl === 'string' && row.imageUrl) ||
          (typeof row.previewUrl === 'string' && row.previewUrl) ||
          '';
        addItem(
          row.id,
          url,
          row.name || row.assetName || row.type || row.id,
        );
      },
    );

    assets.forEach((row) => {
      if (!row.previewUrl) return;
      if (
        row.status !== 'approved' &&
        row.status !== 'needs_review' &&
        row.status !== 'generating'
      ) {
        return;
      }
      addItem(row.id, row.previewUrl, row.id);
    });

    return [...byUrl.values()];
  }, [projectGalleryAssets, assets]);

  const [promptSavingId, setPromptSavingId] = useState('');
  const [regenerateBusyId, setRegenerateBusyId] = useState('');
  const [cancelBusyId, setCancelBusyId] = useState('');
  const [anchorBusy, setAnchorBusy] = useState(false);
  const [anchorDialog, setAnchorDialog] = useState(null);
  const [anchorCategory, setAnchorCategory] = useState('protagonist');
  const [externalAnchorOpen, setExternalAnchorOpen] = useState(false);
  const [externalAnchorTab, setExternalAnchorTab] = useState(0);
  const [externalCategory, setExternalCategory] = useState('protagonist');
  const [externalFile, setExternalFile] = useState(null);
  const [selectedGalleryUrl, setSelectedGalleryUrl] = useState('');
  const [anchorSuccess, setAnchorSuccess] = useState('');
  const [referenceUploadBusyId, setReferenceUploadBusyId] = useState('');
  /** Optimistic referenceUrl overrides keyed by asset id (`null` = cleared). */
  /** @type {[Record<string, string|null>, function]} */
  const [referenceOverrides, setReferenceOverrides] = useState({});
  /** @type {[{ anchorEl: Element, assetId: string, manifestId: string, status: string }|null, function]} */
  const [referenceMenu, setReferenceMenu] = useState(null);
  /** @type {[{ assetId: string, manifestId: string }|null, function]} */
  const [rowGalleryPicker, setRowGalleryPicker] = useState(null);
  const [rowGallerySelectedUrl, setRowGallerySelectedUrl] = useState('');
  /** @type {React.MutableRefObject<{ assetId: string, manifestId: string }|null>} */
  const referenceUploadTargetRef = useRef(null);
  /** @type {React.MutableRefObject<HTMLInputElement|null>} */
  const referenceFileInputRef = useRef(null);
  /** Live prompt editors keyed by asset id (captures unblurred edits before regenerate). */
  /** @type {React.MutableRefObject<Record<string, HTMLInputElement|HTMLTextAreaElement|null>>} */
  const promptInputRefs = useRef({});
  /** @type {[object|null, function]} Row open in Remove Background modal */
  const [imageEditRow, setImageEditRow] = useState(null);
  const [imageEditBusy, setImageEditBusy] = useState(false);
  const [imageEditError, setImageEditError] = useState('');

  const selectableAssets = useMemo(
    () => assets.filter((row) => SELECTABLE_STATUSES.has(row.status)),
    [assets],
  );

  const selectableIds = useMemo(
    () => selectableAssets.map((row) => row.id),
    [selectableAssets],
  );

  const allSelectableChecked =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selected.includes(id));
  const someSelectableChecked =
    selectableIds.some((id) => selected.includes(id)) && !allSelectableChecked;

  const selectedRows = useMemo(
    () => assets.filter((row) => selected.includes(row.id)),
    [assets, selected],
  );

  const repoLinked = Boolean(
    projectDoc &&
      typeof projectDoc.githubOwner === 'string' &&
      projectDoc.githubOwner.trim() &&
      typeof projectDoc.githubRepoName === 'string' &&
      projectDoc.githubRepoName.trim(),
  );

  const pushUi = getPushToGithubUiState({
    selectedRows,
    repoLinked,
    pushBusy,
    bulkBusy,
  });

  const handlePushToGithub = async () => {
    if (!pushUi.canPush || !resolvedProjectId) return;
    setPushBusy(true);
    setPushMessage('');
    setBulkError('');
    try {
      const assetIds = selectedRows
        .filter((row) => row.manifestId && row.id)
        .map((row) => galleryDocIdForManifestAsset(row.manifestId, row.id));
      if (!assetIds.length) {
        setBulkError('No gallery assets mapped for the current selection.');
        return;
      }
      const result = await pushGameAssetsToGithubFn({
        projectId: resolvedProjectId,
        assetIds,
      });
      const data = result?.data || {};
      const count = Array.isArray(data.pushed) ? data.pushed.length : 0;
      const sha =
        typeof data.commitSha === 'string' ? data.commitSha.slice(0, 7) : '';
      setPushMessage(
        sha
          ? `Pushed ${count} asset(s) @ ${sha}`
          : `Pushed ${count} asset(s)`,
      );
    } catch (err) {
      console.error('AssetQueueTable push to GitHub:', err);
      const msg =
        err?.message ||
        err?.details ||
        (typeof err?.code === 'string' ? err.code : '') ||
        'Push to GitHub failed.';
      setBulkError(String(msg));
    } finally {
      setPushBusy(false);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected((prev) => {
        const next = new Set(prev);
        selectableIds.forEach((id) => next.add(id));
        return [...next];
      });
      return;
    }
    setSelected((prev) => prev.filter((id) => !selectableIds.includes(id)));
  };

  const handleRowCheckboxToggle = (assetId) => {
    setSelected((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId],
    );
  };

  /**
   * Batch approve or delete selected assets inside their parent manifests.
   * @param {'approve'|'delete'|'queue'} actionType
   */
  const handleBulkAction = async (actionType) => {
    if (!resolvedCompanyId || selected.length === 0 || bulkBusy) return;

    const selectedSet = new Set(selected);
    /** @type {Map<string, Set<string>>} */
    const byManifest = new Map();
    assets.forEach((row) => {
      if (!selectedSet.has(row.id) || !row.manifestId) return;
      if (!byManifest.has(row.manifestId)) {
        byManifest.set(row.manifestId, new Set());
      }
      byManifest.get(row.manifestId).add(row.id);
    });

    if (byManifest.size === 0) return;

    if (actionType === 'delete' && repoLinked) {
      const selectedSetForRisk = new Set(selected);
      const riskKeys = [];
      assets.forEach((row) => {
        if (!selectedSetForRisk.has(row.id) || !row.imageUrl) return;
        const risk = evaluateSoleSemanticKeyHolder(row, assets);
        if (risk.sole && risk.semanticKey) {
          riskKeys.push(risk.semanticKey);
        }
      });
      if (riskKeys.length > 0) {
        const unique = [...new Set(riskKeys)];
        const ok = window.confirm(
          `Playtest warning: deleting the only asset(s) that map to ` +
            `texture key(s): ${unique.join(', ')}.\n\n` +
            `This project's linked GitHub game loads those fixed keys. ` +
            `After delete, Playtest may show missing sprites (green boxes).\n\n` +
            `Keep a variant (or re-save under the same semantic key) before ` +
            `removing the last holder.\n\n` +
            `Delete anyway?`,
        );
        if (!ok) return;
      }
    }

    setBulkBusy(true);
    setBulkError('');
    try {
      await Promise.all(
        [...byManifest.entries()].map(async ([manifestId, idSet]) => {
          const manifestRef = doc(
            db,
            'companies',
            resolvedCompanyId,
            'game_manifests',
            manifestId,
          );
          const snap = await getDoc(manifestRef);
          if (!snap.exists()) return;

          const data = snap.data() || {};
          const current = Array.isArray(data.assets) ? data.assets : [];
          const uid = auth.currentUser?.uid || null;

          let updatedAssets;
          if (actionType === 'approve') {
            updatedAssets = current.map((asset, index) => {
              const assetId = resolveAssetId(asset, manifestId, index);
              if (!idSet.has(assetId)) return asset;
              const status =
                typeof asset.status === 'string' ? asset.status : 'draft';
              // Never approve in-flight generation rows.
              if (status === 'generating' || status === 'pending') {
                return asset;
              }
              return {
                ...asset,
                id: assetId,
                status: 'approved',
                errorMessage: null,
              };
            });
          } else if (actionType === 'queue') {
            updatedAssets = current.map((asset, index) => {
              const assetId = resolveAssetId(asset, manifestId, index);
              if (!idSet.has(assetId)) return asset;
              const status =
                typeof asset.status === 'string' ? asset.status : 'draft';
              // Wake drafts / failed rows (preserve referenceUrl).
              if (status !== 'draft' && status !== 'failed') return asset;
              return {
                ...asset,
                id: assetId,
                status: 'pending',
                previewUrl: null,
                imageUrl: null,
                errorMessage: null,
              };
            });
          } else if (actionType === 'cancel') {
            updatedAssets = current.map((asset, index) => {
              const assetId = resolveAssetId(asset, manifestId, index);
              if (!idSet.has(assetId)) return asset;
              const status =
                typeof asset.status === 'string' ? asset.status : 'draft';
              if (!CANCELLABLE_STATUSES.has(status)) return asset;
              return {
                ...asset,
                id: assetId,
                status: 'draft',
                errorMessage: null,
              };
            });
          } else if (actionType === 'delete') {
            /** @type {Array<{ asset: object, assetId: string }>} */
            const toDelete = [];
            current.forEach((asset, index) => {
              const assetId = resolveAssetId(asset, manifestId, index);
              if (idSet.has(assetId)) toDelete.push({ asset, assetId });
            });
            await Promise.all(
              toDelete.map(async ({ asset, assetId }) => {
                try {
                  await deleteProjectGalleryAssetClient({
                    companyId: resolvedCompanyId,
                    projectId: resolvedProjectId,
                    manifestId,
                    assetId,
                    storagePath:
                      typeof asset.storagePath === 'string'
                        ? asset.storagePath
                        : null,
                  });
                } catch (galleryErr) {
                  console.warn(
                    'AssetQueueTable gallery delete skipped:',
                    assetId,
                    galleryErr,
                  );
                }
              }),
            );
            updatedAssets = current.filter((asset, index) => {
              const assetId = resolveAssetId(asset, manifestId, index);
              return !idSet.has(assetId);
            });
          } else {
            return;
          }

          await updateDoc(manifestRef, {
            assets: updatedAssets,
            updatedAt: serverTimestamp(),
          });

          if (actionType === 'approve' || actionType === 'queue' || actionType === 'cancel') {
            await Promise.all(
              updatedAssets.map(async (asset, index) => {
                const assetId = resolveAssetId(asset, manifestId, index);
                if (!idSet.has(assetId)) return;
                try {
                  await upsertProjectGalleryAssetClient({
                    companyId: resolvedCompanyId,
                    projectId: resolvedProjectId,
                    manifestId,
                    asset: { ...asset, id: assetId },
                    replaceStorage: actionType === 'queue',
                    uid,
                  });
                } catch (galleryErr) {
                  console.warn(
                    'AssetQueueTable gallery upsert skipped:',
                    assetId,
                    galleryErr,
                  );
                }
              }),
            );
          }
        }),
      );
      setSelected([]);
    } catch (err) {
      console.error('AssetQueueTable bulk action:', err);
      setBulkError(
        err?.message ||
          (actionType === 'delete'
            ? 'Could not delete selected assets.'
            : actionType === 'queue'
              ? 'Could not queue selected drafts for generation.'
              : 'Could not approve selected assets.'),
      );
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Persist an edited generation prompt for one manifest asset.
   * @param {string} manifestId
   * @param {string} assetId
   * @param {string} nextPrompt
   * @param {string} previousPrompt
   */
  const handlePromptBlur = async (
    manifestId,
    assetId,
    nextPrompt,
    previousPrompt,
  ) => {
    const trimmed =
      typeof nextPrompt === 'string' ? nextPrompt.trim() : '';
    const prior =
      typeof previousPrompt === 'string' ? previousPrompt.trim() : '';
    if (!resolvedCompanyId || !manifestId || !assetId || trimmed === prior) {
      return;
    }
    if (promptSavingId === assetId) return;

    setPromptSavingId(assetId);
    setBulkError('');
    try {
      const manifestRef = doc(
        db,
        'companies',
        resolvedCompanyId,
        'game_manifests',
        manifestId,
      );
      const snap = await getDoc(manifestRef);
      if (!snap.exists()) {
        throw new Error('Manifest not found.');
      }

      const data = snap.data() || {};
      const current = Array.isArray(data.assets) ? data.assets : [];
      let found = false;
      const updatedAssets = current.map((asset, index) => {
        const rowId = resolveAssetId(asset, manifestId, index);
        if (rowId !== assetId) return asset;
        found = true;
        return {
          ...asset,
          id: rowId,
          prompt: trimmed,
        };
      });

      if (!found) {
        throw new Error('Asset not found in manifest.');
      }

      await updateDoc(manifestRef, {
        assets: updatedAssets,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('AssetQueueTable prompt save:', err);
      setBulkError(err?.message || 'Could not save prompt.');
    } finally {
      setPromptSavingId('');
    }
  };

  /**
   * Re-queue a single asset for generation using the latest prompt editor value.
   * @param {object} row
   */
  const handleRegenerateAsset = async (row) => {
    const manifestId =
      typeof row?.manifestId === 'string' ? row.manifestId : '';
    const assetId = typeof row?.id === 'string' ? row.id : '';
    if (!resolvedCompanyId || !manifestId || !assetId) return;
    if (
      row.status === 'generating' ||
      regenerateBusyId ||
      cancelBusyId ||
      bulkBusy ||
      promptSavingId === assetId
    ) {
      return;
    }

    const liveValue = promptInputRefs.current[assetId]?.value;
    const nextPrompt =
      typeof liveValue === 'string'
        ? liveValue.trim()
        : typeof row.prompt === 'string'
          ? row.prompt.trim()
          : '';

    if (!nextPrompt) {
      setBulkError('Add a prompt before regenerating this asset.');
      return;
    }

    setRegenerateBusyId(assetId);
    setBulkError('');
    try {
      const manifestRef = doc(
        db,
        'companies',
        resolvedCompanyId,
        'game_manifests',
        manifestId,
      );
      const snap = await getDoc(manifestRef);
      if (!snap.exists()) {
        throw new Error('Manifest not found.');
      }

      const data = snap.data() || {};
      const current = Array.isArray(data.assets) ? data.assets : [];
      let found = false;
      let previousStoragePath = null;
      const updatedAssets = current.map((asset, index) => {
        const rowId = resolveAssetId(asset, manifestId, index);
        if (rowId !== assetId) return asset;
        found = true;
        previousStoragePath =
          typeof asset.storagePath === 'string' ? asset.storagePath : null;
        return {
          ...asset,
          id: rowId,
          prompt: nextPrompt,
          status: 'pending',
          previewUrl: null,
          imageUrl: null,
          storagePath: null,
          errorMessage: null,
        };
      });

      if (!found) {
        throw new Error('Asset not found in manifest.');
      }

      await updateDoc(manifestRef, {
        assets: updatedAssets,
        updatedAt: serverTimestamp(),
      });

      const regenerated = updatedAssets.find(
        (asset, index) => resolveAssetId(asset, manifestId, index) === assetId,
      );
      try {
        await upsertProjectGalleryAssetClient({
          companyId: resolvedCompanyId,
          projectId: resolvedProjectId,
          manifestId,
          asset: regenerated || {
            id: assetId,
            prompt: nextPrompt,
            status: 'pending',
            imageUrl: null,
            previewUrl: null,
            storagePath: null,
          },
          replaceStorage: true,
          uid: auth.currentUser?.uid || null,
        });
        if (previousStoragePath) {
          // Extra cleanup if gallery helper already ran without path.
          try {
            await deleteObject(ref(storage, previousStoragePath));
          } catch {
            /* ignore orphan cleanup */
          }
        }
      } catch (galleryErr) {
        console.warn('AssetQueueTable regenerate gallery sync:', galleryErr);
      }
    } catch (err) {
      console.error('AssetQueueTable regenerate:', err);
      setBulkError(err?.message || 'Could not regenerate asset.');
    } finally {
      setRegenerateBusyId('');
    }
  };

  /**
   * Cancel a pending/generating asset back to draft so it can be re-queued later.
   * @param {object} row
   */
  const handleCancelAsset = async (row) => {
    const manifestId =
      typeof row?.manifestId === 'string' ? row.manifestId : '';
    const assetId = typeof row?.id === 'string' ? row.id : '';
    if (!resolvedCompanyId || !manifestId || !assetId) return;
    if (
      !CANCELLABLE_STATUSES.has(row.status) ||
      cancelBusyId ||
      regenerateBusyId ||
      bulkBusy
    ) {
      return;
    }

    setCancelBusyId(assetId);
    setBulkError('');
    try {
      const manifestRef = doc(
        db,
        'companies',
        resolvedCompanyId,
        'game_manifests',
        manifestId,
      );
      const snap = await getDoc(manifestRef);
      if (!snap.exists()) {
        throw new Error('Manifest not found.');
      }

      const data = snap.data() || {};
      const current = Array.isArray(data.assets) ? data.assets : [];
      let found = false;
      let cancelledAsset = null;
      const updatedAssets = current.map((asset, index) => {
        const rowId = resolveAssetId(asset, manifestId, index);
        if (rowId !== assetId) return asset;
        const status =
          typeof asset.status === 'string' ? asset.status : 'draft';
        if (!CANCELLABLE_STATUSES.has(status)) return asset;
        found = true;
        cancelledAsset = {
          ...asset,
          id: rowId,
          status: 'draft',
          errorMessage: null,
        };
        return cancelledAsset;
      });

      if (!found || !cancelledAsset) {
        throw new Error('Asset is not pending or generating.');
      }

      await updateDoc(manifestRef, {
        assets: updatedAssets,
        updatedAt: serverTimestamp(),
      });

      try {
        await upsertProjectGalleryAssetClient({
          companyId: resolvedCompanyId,
          projectId: resolvedProjectId,
          manifestId,
          asset: cancelledAsset,
          replaceStorage: false,
          uid: auth.currentUser?.uid || null,
        });
      } catch (galleryErr) {
        console.warn('AssetQueueTable cancel gallery sync:', galleryErr);
      }
    } catch (err) {
      console.error('AssetQueueTable cancel:', err);
      setBulkError(err?.message || 'Could not cancel asset generation.');
    } finally {
      setCancelBusyId('');
    }
  };

  /**
   * Inject referenceUrl into pending/needs_review assets of one category.
   * @param {{
   *   category: string,
   *   referenceUrl: string,
   *   excludeAssetId?: string | null,
   * }} args
   * @returns {Promise<number>}
   */
  const applyCategoryAnchor = async ({
    category,
    referenceUrl,
    excludeAssetId = null,
  }) => {
    const catKey = normalizeCategoryKey(category);
    const refUrl = typeof referenceUrl === 'string' ? referenceUrl.trim() : '';
    if (!resolvedCompanyId || !catKey || !refUrl) return 0;

    const manifestList = Array.isArray(manifests) ? manifests : [];
    let updatedCount = 0;

    await Promise.all(
      manifestList.map(async (manifest) => {
        const manifestId =
          typeof manifest.id === 'string' ? manifest.id : '';
        if (!manifestId) return;

        const current = Array.isArray(manifest.assets) ? manifest.assets : [];
        let changed = false;
        const updatedAssets = current.map((asset, index) => {
          const assetId = resolveAssetId(asset, manifestId, index);
          if (excludeAssetId && assetId === excludeAssetId) return asset;
          if (assetCategoryKey(asset) !== catKey) return asset;
          const status =
            typeof asset.status === 'string' ? asset.status : 'pending';
          if (!ANCHOR_REQUEUE_STATUSES.has(status)) return asset;

          changed = true;
          updatedCount += 1;
          return {
            ...asset,
            id: assetId,
            referenceUrl: refUrl,
            status: 'pending',
            previewUrl: null,
            imageUrl: null,
            errorMessage: null,
          };
        });

        if (!changed) return;

        await updateDoc(
          doc(
            db,
            'companies',
            resolvedCompanyId,
            'game_manifests',
            manifestId,
          ),
          {
            assets: updatedAssets,
            updatedAt: serverTimestamp(),
          },
        );
      }),
    );

    return updatedCount;
  };

  const openSetAsAnchorDialog = (row) => {
    const seedUrl =
      (typeof row.previewUrl === 'string' && row.previewUrl.trim()) || '';
    if (!seedUrl) {
      setBulkError('This approved asset has no preview URL to use as an anchor.');
      return;
    }
    const cat =
      normalizeCategoryKey(row.category) ||
      ANCHOR_CATEGORIES[0];
    setAnchorCategory(
      ANCHOR_CATEGORIES.includes(cat) ? cat : ANCHOR_CATEGORIES[0],
    );
    setAnchorDialog({
      assetId: row.id,
      previewUrl: seedUrl,
      category: row.category || cat,
    });
  };

  const confirmSetAsCategoryAnchor = async () => {
    if (!anchorDialog || anchorBusy) return;
    setAnchorBusy(true);
    setBulkError('');
    setAnchorSuccess('');
    try {
      const count = await applyCategoryAnchor({
        category: anchorCategory,
        referenceUrl: anchorDialog.previewUrl,
        excludeAssetId: anchorDialog.assetId,
      });
      setAnchorDialog(null);
      setAnchorSuccess(
        count > 0
          ? `Anchored ${count} ${anchorCategory} asset${count === 1 ? '' : 's'} — re-queued as pending.`
          : `No pending/needs_review assets found in category "${anchorCategory}".`,
      );
    } catch (err) {
      console.error('AssetQueueTable set category anchor:', err);
      setBulkError(err?.message || 'Could not set category anchor.');
    } finally {
      setAnchorBusy(false);
    }
  };

  const confirmExternalAnchorUpload = async () => {
    if (!resolvedCompanyId || !resolvedProjectId || anchorBusy) {
      return;
    }

    const fromGallery =
      externalAnchorTab === 1 &&
      typeof selectedGalleryUrl === 'string' &&
      selectedGalleryUrl.trim();

    if (externalAnchorTab === 0 && !externalFile) return;
    if (externalAnchorTab === 1 && !fromGallery) return;

    setAnchorBusy(true);
    setBulkError('');
    setAnchorSuccess('');
    try {
      let referenceUrl = '';

      if (fromGallery) {
        referenceUrl = selectedGalleryUrl.trim();
      } else {
        const extMatch = /\.([a-zA-Z0-9]+)$/.exec(externalFile.name || '');
        const ext = (extMatch?.[1] || 'png').toLowerCase();
        const storagePath =
          `companies/${resolvedCompanyId}/games/${resolvedProjectId}/assets/` +
          `anchor_${Date.now()}.${ext}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, externalFile, {
          contentType: externalFile.type || 'image/png',
        });
        referenceUrl = await getDownloadURL(storageRef);
      }

      const count = await applyCategoryAnchor({
        category: externalCategory,
        referenceUrl,
        excludeAssetId: null,
      });
      setExternalAnchorOpen(false);
      setExternalFile(null);
      setSelectedGalleryUrl('');
      setExternalAnchorTab(0);
      setAnchorSuccess(
        count > 0
          ? `Anchor applied to ${count} ${externalCategory} asset${count === 1 ? '' : 's'}.`
          : `Anchor saved, but no pending/needs_review/draft assets in "${externalCategory}".`,
      );
    } catch (err) {
      console.error('AssetQueueTable external anchor:', err);
      setBulkError(err?.message || 'Could not apply external anchor.');
    } finally {
      setAnchorBusy(false);
    }
  };

  /**
   * Open dual-option reference menu for a queue row.
   * @param {React.MouseEvent<HTMLElement>} event
   * @param {{ id: string, manifestId: string, status: string }} row
   */
  const openReferenceMenu = (event, row) => {
    if (!row?.manifestId || !row?.id) return;
    if (referenceUploadBusyId || bulkBusy || anchorBusy) return;
    event.preventDefault();
    event.stopPropagation();
    setReferenceMenu({
      anchorEl: event.currentTarget,
      assetId: row.id,
      manifestId: row.manifestId,
      status: row.status,
    });
  };

  const closeReferenceMenu = () => {
    setReferenceMenu(null);
  };

  /**
   * Open hidden file picker for a draft asset's per-row reference image.
   * @param {{ id: string, manifestId: string, status: string }} row
   */
  const openReferenceUpload = (row) => {
    if (!row?.manifestId || !row?.id) return;
    if (referenceUploadBusyId || bulkBusy || anchorBusy) return;
    referenceUploadTargetRef.current = {
      assetId: row.id,
      manifestId: row.manifestId,
    };
    if (referenceFileInputRef.current) {
      referenceFileInputRef.current.value = '';
      referenceFileInputRef.current.click();
    }
  };

  /**
   * Persist a single asset's referenceUrl (or clear it with null).
   * Optimistic local override is applied immediately.
   * @param {string} manifestId
   * @param {string} assetId
   * @param {string|null} referenceUrl
   * @returns {Promise<void>}
   */
  const patchAssetReferenceUrl = async (manifestId, assetId, referenceUrl) => {
    if (!resolvedCompanyId || !manifestId || !assetId) return;

    const nextUrl =
      typeof referenceUrl === 'string' && referenceUrl.trim()
        ? referenceUrl.trim()
        : null;

    setReferenceOverrides((prev) => ({
      ...prev,
      [assetId]: nextUrl,
    }));

    const manifestRef = doc(
      db,
      'companies',
      resolvedCompanyId,
      'game_manifests',
      manifestId,
    );
    const snap = await getDoc(manifestRef);
    if (!snap.exists()) {
      throw new Error('Manifest not found for this asset.');
    }
    const data = snap.data() || {};
    const current = Array.isArray(data.assets) ? data.assets : [];
    const updatedAssets = current.map((asset, index) => {
      const id = resolveAssetId(asset, manifestId, index);
      if (id !== assetId) return asset;
      return {
        ...asset,
        id,
        referenceUrl: nextUrl,
      };
    });

    await updateDoc(manifestRef, {
      assets: updatedAssets,
      updatedAt: serverTimestamp(),
    });
  };

  /**
   * Clear referenceUrl on one asset without deleting the asset row.
   * @param {{ id: string, manifestId: string }} row
   */
  const handleRemoveReference = async (row) => {
    if (!row?.manifestId || !row?.id) return;
    if (referenceUploadBusyId || bulkBusy || anchorBusy) return;
    setReferenceUploadBusyId(row.id);
    setBulkError('');
    try {
      await patchAssetReferenceUrl(row.manifestId, row.id, null);
    } catch (err) {
      console.error('AssetQueueTable remove reference:', err);
      setReferenceOverrides((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setBulkError(err?.message || 'Could not remove reference image.');
    } finally {
      setReferenceUploadBusyId('');
    }
  };

  /**
   * Open per-row gallery picker for setting referenceUrl.
   * @param {{ id: string, manifestId: string }} row
   */
  const openRowGalleryPicker = (row) => {
    if (!row?.manifestId || !row?.id) return;
    setRowGallerySelectedUrl('');
    setRowGalleryPicker({
      assetId: row.id,
      manifestId: row.manifestId,
    });
  };

  const confirmRowGalleryPicker = async () => {
    if (!rowGalleryPicker?.assetId || !rowGalleryPicker?.manifestId) return;
    const url =
      typeof rowGallerySelectedUrl === 'string'
        ? rowGallerySelectedUrl.trim()
        : '';
    if (!url) return;

    setReferenceUploadBusyId(rowGalleryPicker.assetId);
    setBulkError('');
    try {
      await patchAssetReferenceUrl(
        rowGalleryPicker.manifestId,
        rowGalleryPicker.assetId,
        url,
      );
      setRowGalleryPicker(null);
      setRowGallerySelectedUrl('');
    } catch (err) {
      console.error('AssetQueueTable gallery reference:', err);
      setReferenceOverrides((prev) => {
        const next = { ...prev };
        delete next[rowGalleryPicker.assetId];
        return next;
      });
      setBulkError(err?.message || 'Could not apply gallery reference.');
    } finally {
      setReferenceUploadBusyId('');
    }
  };

  /**
   * Upload PNG/JPG reference and set referenceUrl on that draft asset.
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleReferenceFileSelected = async (event) => {
    const file = event.target.files?.[0] || null;
    const target = referenceUploadTargetRef.current;
    event.target.value = '';
    referenceUploadTargetRef.current = null;

    if (!file || !target?.assetId || !target?.manifestId) return;
    if (!resolvedCompanyId || !resolvedProjectId) return;

    const mime = typeof file.type === 'string' ? file.type.toLowerCase() : '';
    const name = typeof file.name === 'string' ? file.name.toLowerCase() : '';
    const okType =
      mime === 'image/png' ||
      mime === 'image/jpeg' ||
      mime === 'image/jpg' ||
      mime === 'image/webp' ||
      /\.png$/.test(name) ||
      /\.jpe?g$/.test(name) ||
      /\.webp$/.test(name);
    if (!okType) {
      setBulkError('Reference upload accepts PNG, JPG, or WebP images only.');
      return;
    }

    setReferenceUploadBusyId(target.assetId);
    setBulkError('');
    try {
      const extMatch = /\.([a-zA-Z0-9]+)$/.exec(file.name || '');
      const ext = (extMatch?.[1] || 'png').toLowerCase();
      const safeId = String(target.assetId)
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .slice(0, 80);
      const storagePath =
        `companies/${resolvedCompanyId}/games/${resolvedProjectId}/assets/` +
        `ref_${safeId}_${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, {
        contentType: file.type || 'image/png',
      });
      const downloadUrl = await getDownloadURL(storageRef);

      await patchAssetReferenceUrl(
        target.manifestId,
        target.assetId,
        downloadUrl,
      );
    } catch (err) {
      console.error('AssetQueueTable reference upload:', err);
      setReferenceOverrides((prev) => {
        const next = { ...prev };
        delete next[target.assetId];
        return next;
      });
      setBulkError(err?.message || 'Could not upload reference image.');
    } finally {
      setReferenceUploadBusyId('');
    }
  };

  /**
   * Open the session image editor for a queue row that has a preview.
   * @param {object} row
   */
  const handleOpenImageEditor = (row) => {
    if (!row?.previewUrl || imageEditBusy) return;
    setImageEditError('');
    setImageEditRow(row);
  };

  /**
   * Persist an edited PNG as a NEW manifest asset variant.
   * Never mutates the source asset's previewUrl / imageUrl / storagePath.
   * @param {Blob} blob
   * @param {{ suffix?: string, fileStem?: string }} [meta]
   */
  const handleSaveImageEdit = async (blob, meta = {}) => {
    const row = imageEditRow;
    const manifestId = typeof row?.manifestId === 'string' ? row.manifestId : '';
    const sourceAssetId = typeof row?.id === 'string' ? row.id : '';
    if (
      !resolvedCompanyId ||
      !resolvedProjectId ||
      !manifestId ||
      !sourceAssetId ||
      !blob
    ) {
      return;
    }

    setImageEditBusy(true);
    setImageEditError('');
    try {
      await saveEditedAssetVariant({
        companyId: resolvedCompanyId,
        projectId: resolvedProjectId,
        blob,
        meta,
        sourceAsset: {
          id: sourceAssetId,
          manifestId,
          type: typeof row.type === 'string' ? row.type : undefined,
          category: typeof row.category === 'string' ? row.category : undefined,
          prompt: typeof row.prompt === 'string' ? row.prompt : undefined,
        },
        uid: auth.currentUser?.uid || null,
      });
      setImageEditRow(null);
    } catch (err) {
      console.error('AssetQueueTable image edit save:', err);
      setImageEditError(
        err?.message || 'Could not save the edited image variant.',
      );
    } finally {
      setImageEditBusy(false);
    }
  };

  if (!resolvedCompanyId || !resolvedProjectId) {
    return (
      <Alert severity="info" sx={{ maxWidth: 1100 }}>
        Select a game project to load its live render queue from Firestore.
      </Alert>
    );
  }

  const colSpan = 7;

  return (
    <Box sx={{ width: '100%', maxWidth: 1100 }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {loading
            ? 'Loading…'
            : `${assets.length} asset${assets.length === 1 ? '' : 's'}`}
        </Typography>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error.message || 'Could not load game manifests.'}
        </Alert>
      ) : null}

      {bulkError ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setBulkError('')}>
          {bulkError}
        </Alert>
      ) : null}

      {imageEditError ? (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          onClose={() => setImageEditError('')}
        >
          {imageEditError}
        </Alert>
      ) : null}

      {pushMessage ? (
        <Alert
          severity="success"
          sx={{ mb: 1 }}
          onClose={() => setPushMessage('')}
        >
          {pushMessage}
        </Alert>
      ) : null}

      {anchorSuccess ? (
        <Alert
          severity="success"
          sx={{ mb: 1 }}
          onClose={() => setAnchorSuccess('')}
        >
          {anchorSuccess}
        </Alert>
      ) : null}

      <Toolbar
        variant="dense"
        sx={{
          mb: 1,
          minHeight: 40,
          px: 1.5,
          borderRadius: 1,
          bgcolor: selected.length > 0 ? 'action.selected' : 'action.hover',
          border: 1,
          borderColor: 'divider',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 120 }}>
          {selected.length > 0
            ? `${selected.length} selected`
            : 'Seed & Derive'}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={anchorBusy || bulkBusy}
          startIcon={<UploadFileOutlinedIcon fontSize="small" />}
          onClick={() => {
            setExternalFile(null);
            setSelectedGalleryUrl('');
            setExternalAnchorTab(0);
            setExternalCategory('protagonist');
            setExternalAnchorOpen(true);
          }}
          sx={{ textTransform: 'none' }}
        >
          Upload External Anchor
        </Button>
        {selected.length > 0 ? (
          <>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={bulkBusy || anchorBusy || pushBusy}
              startIcon={
                bulkBusy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AutoAwesomeOutlinedIcon fontSize="small" />
                )
              }
              onClick={() => handleBulkAction('queue')}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Generate Selected
            </Button>
            <Button
              size="small"
              color="success"
              variant="contained"
              disabled={bulkBusy || anchorBusy || pushBusy}
              startIcon={
                bulkBusy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CheckIcon fontSize="small" />
                )
              }
              onClick={() => handleBulkAction('approve')}
              sx={{ textTransform: 'none' }}
            >
              Approve Selected
            </Button>
            <Tooltip title={pushUi.tooltip}>
              <span>
                <Button
                  size="small"
                  color="info"
                  variant="contained"
                  disabled={!pushUi.canPush || anchorBusy}
                  startIcon={
                    pushBusy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CloudUploadOutlinedIcon fontSize="small" />
                    )
                  }
                  onClick={handlePushToGithub}
                  sx={{ textTransform: 'none' }}
                >
                  Push selected to GitHub
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={bulkBusy || anchorBusy || pushBusy}
              startIcon={
                bulkBusy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CancelOutlinedIcon fontSize="small" />
                )
              }
              onClick={() => handleBulkAction('cancel')}
              sx={{ textTransform: 'none' }}
            >
              Cancel Selected
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={bulkBusy || anchorBusy}
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              onClick={() => handleBulkAction('delete')}
              sx={{ textTransform: 'none' }}
            >
              Delete Selected
            </Button>
          </>
        ) : null}
      </Toolbar>

      <TableContainer
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Table size="small" stickyHeader aria-label="Asset render queue">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ py: dense ? 0.25 : 0.5, width: 42 }}>
                <Checkbox
                  size="small"
                  color="primary"
                  indeterminate={someSelectableChecked}
                  checked={allSelectableChecked}
                  disabled={selectableIds.length === 0 || bulkBusy}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'Select all reviewable assets' }}
                />
              </TableCell>
              <TableCell sx={{ py: dense ? 0.5 : 1, fontWeight: 700, width: 140 }}>
                Asset ID
              </TableCell>
              <TableCell sx={{ py: dense ? 0.5 : 1, fontWeight: 700, width: 110 }}>
                Category
              </TableCell>
              <TableCell sx={{ py: dense ? 0.5 : 1, fontWeight: 700 }}>
                Prompt
              </TableCell>
              <TableCell sx={{ py: dense ? 0.5 : 1, fontWeight: 700, width: 120 }}>
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{ py: dense ? 0.5 : 1, fontWeight: 700, width: 72 }}
              >
                Reference
              </TableCell>
              <TableCell
                align="right"
                sx={{ py: dense ? 0.5 : 1, fontWeight: 700, width: 96 }}
              >
                Preview
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : null}
            {!loading && assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No manifest assets for this project yet. Generate a game
                    manifest to populate the queue.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {assets.map((row) => {
              const chip = statusChipProps(row.status);
              const assetId = row.id || '—';
              const category = row.category || '—';
              const prompt = typeof row.prompt === 'string' ? row.prompt : '';
              const previewUrl =
                typeof row.previewUrl === 'string' ? row.previewUrl : '';
              const referenceUrlRaw =
                typeof row.referenceUrl === 'string'
                  ? row.referenceUrl.trim()
                  : '';
              const referenceUrl = Object.prototype.hasOwnProperty.call(
                referenceOverrides,
                assetId,
              )
                ? (typeof referenceOverrides[assetId] === 'string'
                    ? referenceOverrides[assetId].trim()
                    : '')
                : referenceUrlRaw;
              const canEditReference =
                row.status === 'draft' ||
                row.status === 'pending' ||
                row.status === 'needs_review' ||
                row.status === 'failed';
              const isSelected = selected.includes(assetId);
              const canSelect = SELECTABLE_STATUSES.has(row.status);
              const referenceUploading = referenceUploadBusyId === assetId;

              return (
                <TableRow
                  key={`${row.manifestId || 'm'}:${assetId}`}
                  hover
                  selected={isSelected}
                >
                  <TableCell padding="checkbox" sx={{ py: dense ? 0.15 : 0.5 }}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={isSelected}
                      disabled={!canSelect || bulkBusy}
                      onChange={() => handleRowCheckboxToggle(assetId)}
                      onClick={(e) => e.stopPropagation()}
                      inputProps={{
                        'aria-label': `Select asset ${assetId}`,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      py: dense ? 0.35 : 0.75,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {assetId}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: dense ? 0.35 : 0.75,
                      fontSize: '0.75rem',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {category}
                  </TableCell>
                  <TableCell sx={{ py: dense ? 0.35 : 0.75, maxWidth: 280 }}>
                    <Stack direction="row" spacing={0.5} alignItems="flex-start">
                      <InputBase
                        key={`${row.manifestId || 'm'}:${assetId}:prompt`}
                        fullWidth
                        multiline
                        maxRows={3}
                        defaultValue={prompt}
                        placeholder="Generation prompt"
                        disabled={
                          row.status === 'generating' ||
                          bulkBusy ||
                          promptSavingId === assetId ||
                          regenerateBusyId === assetId
                        }
                        inputRef={(el) => {
                          promptInputRefs.current[assetId] = el;
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) =>
                          void handlePromptBlur(
                            row.manifestId,
                            assetId,
                            e.target.value,
                            prompt,
                          )
                        }
                        inputProps={{
                          'aria-label': `Prompt for ${assetId}`,
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          lineHeight: 1.35,
                          color: 'text.secondary',
                          flex: 1,
                          minWidth: 0,
                          '& .MuiInputBase-input': {
                            py: 0,
                            px: 0.25,
                          },
                        }}
                      />
                      {promptSavingId === assetId ||
                      regenerateBusyId === assetId ? (
                        <CircularProgress size={14} sx={{ mt: 0.25, flexShrink: 0 }} />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: dense ? 0.35 : 0.75 }}>
                    <Stack spacing={0.25} alignItems="flex-start">
                      <Chip
                        size="small"
                        label={chip.label}
                        color={chip.color}
                        variant={
                          chip.color === 'default' || row.status === 'draft'
                            ? 'outlined'
                            : 'filled'
                        }
                        sx={{
                          height: 22,
                          fontWeight: 600,
                          ...(row.status === 'draft'
                            ? { color: 'text.secondary', borderColor: 'divider' }
                            : null),
                        }}
                      />
                      {row.status === 'failed' && row.errorMessage ? (
                        <Typography
                          variant="caption"
                          color="error"
                          title={row.errorMessage}
                          sx={{
                            display: 'block',
                            maxWidth: 160,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}
                        >
                          {row.errorMessage}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ py: dense ? 0.25 : 0.75 }}>
                    {referenceUrl ? (
                      <Stack
                        direction="row"
                        spacing={0.25}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Tooltip title="Reference image">
                          <Box
                            component="img"
                            src={referenceUrl}
                            alt=""
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setPreviewImage(referenceUrl);
                              setPreviewMeta({
                                id: `${assetId} (ref)`,
                                category,
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPreviewImage(referenceUrl);
                                setPreviewMeta({
                                  id: `${assetId} (ref)`,
                                  category,
                                });
                              }
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 0.5,
                              objectFit: 'cover',
                              border: 1,
                              borderColor: 'divider',
                              cursor: 'pointer',
                              imageRendering: 'pixelated',
                              bgcolor: 'action.hover',
                              verticalAlign: 'middle',
                            }}
                          />
                        </Tooltip>
                        {canEditReference ? (
                          <Tooltip title="Remove reference">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={
                                  referenceUploading ||
                                  Boolean(referenceUploadBusyId) ||
                                  bulkBusy ||
                                  anchorBusy
                                }
                                aria-label={`Remove reference for ${assetId}`}
                                onClick={() => handleRemoveReference(row)}
                                sx={{ p: 0.25 }}
                              >
                                {referenceUploading ? (
                                  <CircularProgress size={12} />
                                ) : (
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        {canEditReference ? (
                          <Tooltip title="Change reference">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  Boolean(referenceUploadBusyId) ||
                                  bulkBusy ||
                                  anchorBusy
                                }
                                aria-label={`Change reference for ${assetId}`}
                                aria-controls={
                                  referenceMenu?.assetId === assetId
                                    ? 'reference-actions-menu'
                                    : undefined
                                }
                                aria-haspopup="true"
                                aria-expanded={
                                  referenceMenu?.assetId === assetId
                                    ? 'true'
                                    : undefined
                                }
                                onClick={(e) => openReferenceMenu(e, row)}
                                sx={{ p: 0.25 }}
                              >
                                <MoreVertIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    ) : canEditReference ? (
                      <Tooltip title="Add reference">
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              Boolean(referenceUploadBusyId) ||
                              bulkBusy ||
                              anchorBusy
                            }
                            aria-label={`Add reference for ${assetId}`}
                            aria-controls={
                              referenceMenu?.assetId === assetId
                                ? 'reference-actions-menu'
                                : undefined
                            }
                            aria-haspopup="true"
                            aria-expanded={
                              referenceMenu?.assetId === assetId
                                ? 'true'
                                : undefined
                            }
                            onClick={(e) => openReferenceMenu(e, row)}
                            sx={{ p: 0.35 }}
                          >
                            {referenceUploading ? (
                              <CircularProgress size={14} />
                            ) : (
                              <AddPhotoAlternateOutlinedIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: dense ? 0.25 : 0.75 }}>
                    <Stack
                      direction="row"
                      spacing={0.25}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      {previewUrl ? (
                        <Tooltip title="Inspect preview">
                          <Box
                            component="img"
                            src={previewUrl}
                            alt=""
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setPreviewImage(previewUrl);
                              setPreviewMeta({
                                id: assetId,
                                category,
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPreviewImage(previewUrl);
                                setPreviewMeta({
                                  id: assetId,
                                  category,
                                });
                              }
                            }}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 0.5,
                              objectFit: 'cover',
                              border: 1,
                              borderColor: 'divider',
                              flexShrink: 0,
                              bgcolor: 'action.hover',
                              cursor: 'pointer',
                              imageRendering: 'pixelated',
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Box
                          aria-hidden
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 0.5,
                            border: '1px dashed',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {previewUrl ? (
                        <Tooltip title="Download">
                          <span>
                            <IconButton
                              size="small"
                              disabled={bulkBusy || anchorBusy}
                              aria-label={`Download ${assetId}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await downloadImageUrl(
                                    previewUrl,
                                    filenameForGalleryAsset({
                                      assetName: assetId,
                                      id: assetId,
                                      previewUrl,
                                      imageUrl: row.imageUrl || previewUrl,
                                    }),
                                  );
                                } catch (err) {
                                  console.error(
                                    'AssetQueueTable download:',
                                    err,
                                  );
                                  setBulkError(
                                    err?.message || 'Download failed.',
                                  );
                                }
                              }}
                              sx={{ p: 0.35 }}
                            >
                              <DownloadOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {previewUrl ? (
                        <Tooltip title="Edit image">
                          <span>
                            <IconButton
                              size="small"
                              disabled={
                                bulkBusy ||
                                anchorBusy ||
                                imageEditBusy ||
                                Boolean(imageEditRow)
                              }
                              aria-label={`Edit image for ${assetId}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenImageEditor(row);
                              }}
                              sx={{ p: 0.35 }}
                            >
                              <EditOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      <Tooltip
                        title={
                          row.referenceUrl
                            ? 'Has reference anchor'
                            : 'No reference anchor'
                        }
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: row.referenceUrl
                              ? 'success.main'
                              : 'transparent',
                            border: row.referenceUrl ? 0 : '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                            display: 'inline-block',
                          }}
                        />
                      </Tooltip>
                      {row.status === 'approved' && previewUrl ? (
                        <Tooltip title="Set as Category Anchor">
                          <IconButton
                            size="small"
                            disabled={anchorBusy || bulkBusy}
                            aria-label={`Set ${assetId} as category anchor`}
                            onClick={() => openSetAsAnchorDialog(row)}
                            sx={{ p: 0.35 }}
                          >
                            <CenterFocusStrongIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Approve (use bulk toolbar)">
                        <span>
                          <IconButton
                            size="small"
                            disabled
                            aria-label={`Approve ${assetId}`}
                            sx={{ p: 0.35 }}
                          >
                            <CheckIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {CANCELLABLE_STATUSES.has(row.status) ? (
                        <Tooltip title="Cancel generation (return to draft)">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={
                                bulkBusy ||
                                Boolean(cancelBusyId) ||
                                Boolean(regenerateBusyId) ||
                                promptSavingId === assetId
                              }
                              aria-label={`Cancel ${assetId}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleCancelAsset(row);
                              }}
                              sx={{ p: 0.35 }}
                            >
                              {cancelBusyId === assetId ? (
                                <CircularProgress size={14} />
                              ) : (
                                <CancelOutlinedIcon fontSize="inherit" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Regenerate with current prompt">
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              row.status === 'generating' ||
                              bulkBusy ||
                              Boolean(regenerateBusyId) ||
                              Boolean(cancelBusyId) ||
                              promptSavingId === assetId
                            }
                            aria-label={`Regenerate ${assetId}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRegenerateAsset(row);
                            }}
                            sx={{ p: 0.35 }}
                          >
                            {regenerateBusyId === assetId ? (
                              <CircularProgress size={14} />
                            ) : (
                              <RefreshIcon fontSize="inherit" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <input
        ref={referenceFileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
        hidden
        onChange={handleReferenceFileSelected}
      />

      <Menu
        id="reference-actions-menu"
        anchorEl={referenceMenu?.anchorEl || null}
        open={Boolean(referenceMenu?.anchorEl)}
        onClose={closeReferenceMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem
          dense
          onClick={() => {
            const target = referenceMenu;
            closeReferenceMenu();
            if (!target) return;
            openReferenceUpload({
              id: target.assetId,
              manifestId: target.manifestId,
              status: target.status,
            });
          }}
        >
          <UploadFileOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Upload from PC
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            const target = referenceMenu;
            closeReferenceMenu();
            if (!target) return;
            openRowGalleryPicker({
              id: target.assetId,
              manifestId: target.manifestId,
            });
          }}
        >
          <CollectionsOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Select from Gallery
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(rowGalleryPicker)}
        onClose={() => {
          if (!referenceUploadBusyId) {
            setRowGalleryPicker(null);
            setRowGallerySelectedUrl('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5 }}>Select Reference from Gallery</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pick an existing approved or rendered asset to use as this row&apos;s
            reference image.
          </Typography>
          {projectGalleryLoading && galleryItems.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
          {!projectGalleryLoading && galleryItems.length === 0 ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              No gallery images yet. Approve or generate assets first, or upload
              from PC.
            </Alert>
          ) : null}
          <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.5 }}>
            <Grid container spacing={1}>
              {galleryItems.map((item) => {
                const selected = rowGallerySelectedUrl === item.url;
                return (
                  <Grid key={`row-ref-${item.id}-${item.url}`} size={{ xs: 4, sm: 3 }}>
                    <Tooltip title={item.label}>
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setRowGallerySelectedUrl(item.url)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setRowGallerySelectedUrl(item.url);
                          }
                        }}
                        sx={{
                          width: '100%',
                          aspectRatio: '1 / 1',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border: 2,
                          borderColor: selected ? 'primary.main' : 'divider',
                          boxShadow: selected ? 2 : 0,
                          bgcolor: 'action.hover',
                          outline: selected ? '1px solid' : 'none',
                          outlineColor: 'primary.main',
                        }}
                      >
                        <Box
                          component="img"
                          src={item.url}
                          alt={item.label}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            imageRendering: 'pixelated',
                            display: 'block',
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => {
              setRowGalleryPicker(null);
              setRowGallerySelectedUrl('');
            }}
            disabled={Boolean(referenceUploadBusyId)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmRowGalleryPicker}
            disabled={
              !rowGallerySelectedUrl.trim() || Boolean(referenceUploadBusyId)
            }
            startIcon={
              referenceUploadBusyId ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CollectionsOutlinedIcon fontSize="small" />
              )
            }
            sx={{ textTransform: 'none' }}
          >
            Apply Reference
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="asset-preview-dialog-title"
      >
        <DialogTitle
          id="asset-preview-dialog-title"
          sx={{
            position: 'relative',
            pr: 6,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} component="span">
            {previewMeta.id || 'Asset preview'}
          </Typography>
          {previewMeta.category ? (
            <Typography variant="caption" color="text.secondary" component="span">
              · {previewMeta.category}
            </Typography>
          ) : null}
          <IconButton
            aria-label="Close preview"
            onClick={() => setPreviewImage(null)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'grey.900',
            py: 3,
          }}
        >
          {previewImage ? (
            <Box
              component="img"
              src={previewImage}
              alt={previewMeta.id || 'Asset preview'}
              sx={{
                width: 400,
                height: 400,
                maxWidth: '100%',
                objectFit: 'contain',
                imageRendering: 'pixelated',
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(anchorDialog)}
        onClose={() => {
          if (!anchorBusy) setAnchorDialog(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5 }}>Set as Category Anchor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inject this approved preview into all pending / needs_review assets
            in the chosen category, then re-queue them for Seed &amp; Derive
            rendering.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="anchor-category-label">Category</InputLabel>
            <Select
              labelId="anchor-category-label"
              label="Category"
              value={anchorCategory}
              onChange={(e) => setAnchorCategory(e.target.value)}
              disabled={anchorBusy}
            >
              {ANCHOR_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setAnchorDialog(null)}
            disabled={anchorBusy}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSetAsCategoryAnchor}
            disabled={anchorBusy}
            startIcon={
              anchorBusy ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{ textTransform: 'none' }}
          >
            Apply Anchor
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={externalAnchorOpen}
        onClose={() => {
          if (!anchorBusy) setExternalAnchorOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5 }}>Set Category Anchor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose a seed image from a local upload or this project&apos;s
            asset gallery, then apply it as <code>referenceUrl</code> for draft
            / pending / needs_review assets in one category.
          </Typography>

          <Tabs
            value={externalAnchorTab}
            onChange={(_e, value) => {
              setExternalAnchorTab(value);
              setBulkError('');
            }}
            variant="fullWidth"
            sx={{ mb: 1.5, minHeight: 36, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label="Upload New File"
              disabled={anchorBusy}
              sx={{ textTransform: 'none', minHeight: 36, py: 0.5 }}
            />
            <Tab
              label="Select from Gallery"
              disabled={anchorBusy}
              sx={{ textTransform: 'none', minHeight: 36, py: 0.5 }}
            />
          </Tabs>

          {externalAnchorTab === 0 ? (
            <Stack spacing={2}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<UploadFileOutlinedIcon />}
                disabled={anchorBusy}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                {externalFile ? externalFile.name : 'Choose image file'}
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setExternalFile(file);
                    setSelectedGalleryUrl('');
                  }}
                />
              </Button>
            </Stack>
          ) : (
            <Box>
              {projectGalleryLoading && galleryItems.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : null}
              {!projectGalleryLoading && galleryItems.length === 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No gallery images yet. Approve or generate assets first, or
                  upload a new file.
                </Alert>
              ) : null}
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                <Grid container spacing={1}>
                  {galleryItems.map((item) => {
                    const selected = selectedGalleryUrl === item.url;
                    return (
                      <Grid key={item.id + item.url} size={{ xs: 4, sm: 3 }}>
                        <Tooltip title={item.label}>
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setSelectedGalleryUrl(item.url);
                              setExternalFile(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedGalleryUrl(item.url);
                                setExternalFile(null);
                              }
                            }}
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1',
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: 2,
                              borderColor: selected ? 'primary.main' : 'divider',
                              boxShadow: selected ? 2 : 0,
                              bgcolor: 'action.hover',
                              outline: selected ? '1px solid' : 'none',
                              outlineColor: 'primary.main',
                            }}
                          >
                            <Box
                              component="img"
                              src={item.url}
                              alt={item.label}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                imageRendering: 'pixelated',
                                display: 'block',
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          )}

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="external-anchor-category-label">
              Category
            </InputLabel>
            <Select
              labelId="external-anchor-category-label"
              label="Category"
              value={externalCategory}
              onChange={(e) => setExternalCategory(e.target.value)}
              disabled={anchorBusy}
            >
              {ANCHOR_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setExternalAnchorOpen(false)}
            disabled={anchorBusy}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmExternalAnchorUpload}
            disabled={
              anchorBusy ||
              (externalAnchorTab === 0
                ? !externalFile
                : !selectedGalleryUrl.trim())
            }
            startIcon={
              anchorBusy ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{ textTransform: 'none' }}
          >
            {externalAnchorTab === 0 ? 'Upload & Apply' : 'Apply Gallery Anchor'}
          </Button>
        </DialogActions>
      </Dialog>

      <ImageEditorModal
        open={Boolean(imageEditRow)}
        imageUrl={
          (typeof imageEditRow?.previewUrl === 'string' &&
            imageEditRow.previewUrl) ||
          ''
        }
        assetLabel={
          (typeof imageEditRow?.id === 'string' && imageEditRow.id) || ''
        }
        busy={imageEditBusy}
        companyId={resolvedCompanyId}
        projectId={resolvedProjectId}
        saveToProjectLabel="Save PNG"
        onClose={() => {
          if (!imageEditBusy) {
            setImageEditRow(null);
            setImageEditError('');
          }
        }}
        onSave={handleSaveImageEdit}
      />
    </Box>
  );
}
